// LunaWorks generations in flight (generate-image, generate-pdf ... —
// lib/api.js sendGenerate), for pages/Luna.jsx (the chat composer's wand)
// and components/DocGeneratorPanel.jsx — kept here so it can be tested on
// its own (generations.test.js).
//
// A deck can take four minutes. The owner's test (2026-10-17..20): with
// nothing on screen for two of them, the page was reloaded and the deck
// asked for again — both were built and both billed. Now:
// - each generation is kept in this tab's sessionStorage while it runs:
//   { key (its X-Idempotency-Key), route, fields (its form's text — there
//   are no files in these forms), mode, conversationId, making, text (the
//   words of its bubble in the chat), origin ("chat" | "panel"), startedAt,
//   owner };
// - a reload, the page back in view or back online picks it up again
//   (pickUpKept, pages/Luna.jsx):
//   the same request under the same key, its time counted from the first
//   click — the server answers 409 while it is still being made and the
//   kept result once done (backend contract C3), never a second build —
//   while the server can still be on it (lib/api.js generateDeadline).
//   Past that nothing is sent under its key (a failed build let it go: a
//   send would build, and bill, it anew); it is looked for in Ürettiklerim
//   instead (lib/api.js findGenerated) — not there, the page says it may
//   have failed (e.mayHaveFailed) and leaves a new request to the user;
// - it is let go once answered or failed, after GENERATION_MAX_AGE_MS, on
//   sign-out (lib/api.js signOut), and when another account is signed in
//   (owner: lib/api.js accountStamp);
// - while one runs, the page asks before it is closed (holdPageOnLeave) and
//   says how long it has taken (FriendPanel's waiting bubble,
//   DocGeneratorPanel).
import { useSyncExternalStore } from "react";
import {
  accountStamp, sendGenerate, findGenerated, generateDeadline, newGenerateKey, GENERATE_ROUTES,
} from "./api";

const STORE_KEY = "luna_pending_generations";
export const GENERATION_MAX_AGE_MS = 8 * 60 * 1000;
// (more than the wand's picture, the wand's file and a panel's file at once)
const MAX_KEPT = 4;
const ROUTES = new Set(Object.values(GENERATE_ROUTES));

// ---- The ones this page is waiting on ----
const running = new Map(); // key -> entry (+ resumed)
let snapshot = [];
const listeners = new Set();

function changed() {
  snapshot = [...running.values()];
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch (_) {} // (a listener's trouble must not stop the others)
  }
}

export function subscribeGenerations(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// The generations this page is waiting on, oldest first (the same array
// until one starts or ends).
export function runningGenerations() {
  return snapshot;
}

export function useRunningGenerations() {
  return useSyncExternalStore(subscribeGenerations, runningGenerations, runningGenerations);
}

export function generationsInFlight() {
  return running.size;
}

export function isRunning(key) {
  return running.has(key);
}

// A new generation, not sent yet: `route` one of lib/api.js GENERATE_ROUTES'
// values, `fields` its text fields.
export function newGeneration({ route, fields, mode, conversationId = null, making = null, text = "", origin = "chat" }, now = Date.now()) {
  return {
    key: newGenerateKey(),
    route,
    fields: { ...(fields || {}) },
    mode: mode === "work" ? "work" : "friend",
    conversationId: typeof conversationId === "string" && conversationId ? conversationId : null,
    making: typeof making === "string" ? making : null,
    text: typeof text === "string" ? text : "",
    origin: origin === "panel" ? "panel" : "chat",
    startedAt: now,
  };
}

// ---- Kept in this tab ----
function readKept() {
  try {
    const list = JSON.parse(sessionStorage.getItem(STORE_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch (_) {
    return [];
  }
}

function writeKept(list) {
  try {
    if (list.length) sessionStorage.setItem(STORE_KEY, JSON.stringify(list));
    else sessionStorage.removeItem(STORE_KEY);
  } catch (_) {} // (private mode, or full: a reload just can't pick it up)
}

// A kept entry as it may be sent again, or null: an unknown shape, too old
// (or from the future), or another account's.
function usable(e, now, owner) {
  if (!e || typeof e !== "object") return null;
  if (typeof e.key !== "string" || !/^gen-[\w-]{8,80}$/.test(e.key) || !ROUTES.has(e.route)) return null;
  if (!e.fields || typeof e.fields !== "object" || Array.isArray(e.fields)) return null;
  if (!Object.values(e.fields).every((v) => typeof v === "string")) return null;
  if (!Number.isFinite(e.startedAt) || now - e.startedAt >= GENERATION_MAX_AGE_MS || e.startedAt - now >= 60000) return null;
  if (typeof owner !== "string" || owner === "" || e.owner !== owner) return null;
  return {
    key: e.key,
    route: e.route,
    fields: { ...e.fields },
    mode: e.mode === "work" ? "work" : "friend",
    conversationId: typeof e.conversationId === "string" && e.conversationId ? e.conversationId : null,
    making: typeof e.making === "string" ? e.making : null,
    text: typeof e.text === "string" ? e.text : "",
    origin: e.origin === "panel" ? "panel" : "chat",
    startedAt: e.startedAt,
  };
}

// Kept before its request goes (a reload a second later still finds it),
// signed with the account it is asked under; nothing is kept while signed
// out.
export function keepGeneration(entry) {
  const owner = accountStamp();
  if (!owner) return;
  const list = readKept().filter((e) => e?.key !== entry.key);
  list.push({ ...entry, owner });
  writeKept(list.slice(-MAX_KEPT));
}

// What the page was told about a generation kept for later ("stillWorking"
// | "connectionLost", by key): picked up again and ending the same way, its
// error says so (e.told) and the page says nothing new — the page back in
// view used to repeat the toast each time.
const told = new Map();

function noteTold(key, e) {
  const said = e?.stillWorking ? "stillWorking" : e?.connectionLost ? "connectionLost" : null;
  if (!said) return;
  if (told.get(key) === said) e.told = true;
  told.set(key, said);
}

export function forgetGeneration(key) {
  told.delete(key);
  const list = readKept();
  const left = list.filter((e) => e?.key !== key);
  if (left.length !== list.length) writeKept(left);
}

// When the server can no longer be on `entry` (lib/api.js generateDeadline).
export function generationDeadline(entry) {
  return generateDeadline(entry.route, entry.startedAt);
}

// The generations this tab kept for the account signed in now, oldest
// first; the rest (unreadable, too old, another account's) are let go.
export function keptGenerations(now = Date.now(), owner = accountStamp()) {
  const list = readKept();
  const out = [];
  const keep = [];
  for (const raw of list) {
    const e = usable(raw, now, owner);
    if (!e) continue;
    out.push(e);
    keep.push(raw);
  }
  if (keep.length !== list.length) writeKept(keep);
  return out;
}

// The messages this page got as a generation's result (sent, or found by
// lookUp): never credited to another one (lib/api.js findGenerated's
// exclude) — the panel and the wand making the same kind at once.
const credited = new Set();
const CREDITED_MAX = 50;
function credit(out) {
  const id = out?.messageId;
  if (id === null || id === undefined || id === "") return;
  credited.add(String(id));
  while (credited.size > CREDITED_MAX) credited.delete(credited.values().next().value);
}

// A file stored right at its deadline may reach Ürettiklerim a moment
// later: one not there when first looked for within this long of the
// deadline is looked for once more this long past it, before the page says
// it may have failed (client review, round 2).
export const LOOK_AGAIN_MS = 30000;

// `entry` looked for in Ürettiklerim once its deadline is past (lib/api.js
// findGenerated): its result, found: true. Not there: e.mayHaveFailed — the
// page offers a new request, it never sends one by itself. The list
// couldn't be read: e.connectionLost when nothing answered (kept, looked for
// again once back), else that answer's error.
async function lookUp(entry) {
  const look = async () => {
    try {
      return await findGenerated(entry.route, entry.fields, { since: entry.startedAt, exclude: [...credited] });
    } catch (e) {
      if (!e?.response) e.connectionLost = true;
      throw e;
    }
  };
  let found = await look();
  const deadline = generationDeadline(entry);
  if (!found && Math.abs(Date.now() - deadline) < LOOK_AGAIN_MS) {
    await new Promise((resolve) => setTimeout(resolve, deadline + LOOK_AGAIN_MS - Date.now()));
    found = await look();
  }
  if (found) return found;
  throw Object.assign(new Error("this generation did not turn up"), { mayHaveFailed: true });
}

// Sends `entry` (newGeneration's, or a kept one picked up again: resumed)
// and resolves with lib/api.js sendGenerate's result. While it runs it is
// in runningGenerations (with `resumed`); it is let go from this tab once
// answered or refused — kept only when the wait ended without an answer
// while the server may still be making it (e.stillWorking: it said 409 and
// then could not be reached; e.connectionLost: nothing came back at all — a
// phone locked for a minute): the page back in view, back online, or at the
// generation's deadline (pages/Luna.jsx) picks it up again. Once its
// deadline is past it is never sent again: lookUp says whether it was made
// (also for one picked up only then). minMs: it stays "running" at least
// this long from startedAt (a picture shown too fast reads as rushed —
// pages/Luna.jsx MIN_IMAGE_GEN_MS).
export async function runGeneration(entry, { resumed = false, minMs = 0 } = {}) {
  if (running.has(entry.key)) throw new Error("this generation is already being waited on");
  if (!resumed) keepGeneration(entry);
  running.set(entry.key, { ...entry, resumed });
  changed();
  try {
    let out;
    try {
      out = resumed && Date.now() >= generationDeadline(entry)
        ? await lookUp(entry)
        : await sendGenerate(entry.route, entry.fields, entry.mode, {
          conversationId: entry.conversationId, idempotencyKey: entry.key, since: entry.startedAt,
        });
    } catch (e) {
      if (!e?.pastDeadline) throw e;
      out = await lookUp(entry);
    }
    forgetGeneration(entry.key);
    credit(out);
    const left =entry.startedAt + minMs - Date.now();
    if (minMs > 0 && left > 0) await new Promise((resolve) => setTimeout(resolve, left));
    return out;
  } catch (e) {
    if (!e?.stillWorking && !e?.connectionLost) forgetGeneration(entry.key);
    else noteTold(entry.key, e);
    throw e;
  } finally {
    running.delete(entry.key);
    changed();
  }
}

// Every kept generation this page isn't waiting on, handed to `pickUp`
// (pages/Luna.jsx: after a reload, with the page back in view or back
// online) — which sends it again with runGeneration(entry, { resumed: true }).
// Returns the ones handed over.
export function pickUpKept(pickUp, now = Date.now()) {
  const due = keptGenerations(now).filter((e) => !running.has(e.key));
  due.forEach((e) => pickUp(e));
  return due;
}

// What the page's beforeunload sets (browsers show their own words; one that
// ignores preventDefault asks only when this is a non-empty string).
export const LEAVE_PROMPT = "Luna is still making your file. Leave anyway?";

// The page's beforeunload (pages/Luna.jsx): while a generation is being
// made here — or `busy`, a typed turn making a file — the browser asks
// before the page goes ("Leave site?"). true when it asked (the handler then
// returns LEAVE_PROMPT too).
export function holdPageOnLeave(event, busy = false) {
  if (running.size === 0 && !busy) return false;
  event.preventDefault();
  // (older browsers ask only when returnValue is a non-empty string)
  event.returnValue = LEAVE_PROMPT;
  return true;
}
